import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { getUrlParam } from 'utils/url';
import { FeatureFlagId, QUERY_FF_PREFIX } from 'types/featureflags';
import { FEATURE_FLAGS_DEFAULTS } from 'featureFlagsDefaults';
import { useServerFeatureFlags } from 'api/featureflags';

type FeatureFlagsState = Record<FeatureFlagId, boolean>;

interface FeatureFlagsContextValue {
	flags: FeatureFlagsState;
	isFeatureFlagActive: (id: FeatureFlagId) => boolean;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue | undefined>(
	undefined
);

export const FeatureFlagsProvider: React.FC<
	React.PropsWithChildren<{
		overrides?: Partial<Record<FeatureFlagId, boolean>>;
	}>
> = ({ children = null, overrides = undefined }) => {
	const { data: serverFeatureFlags } = useServerFeatureFlags();

	const flags = useMemo(() => {
		const calculatedFlags = {} as FeatureFlagsState;

		(Object.values(FeatureFlagId) as FeatureFlagId[]).forEach((flag) => {
			const urlParamKey = `${QUERY_FF_PREFIX}:${flag}`;
			const urlParamValue = getUrlParam(urlParamKey);
			let isActive = FEATURE_FLAGS_DEFAULTS[flag];

			const serverValue = serverFeatureFlags?.features?.[flag];
			if (serverValue !== undefined && serverValue !== null) {
				isActive = !!serverValue;
			}

			/* istanbul ignore next */
			if (urlParamValue === 'true' || urlParamValue === 'false') {
				isActive = urlParamValue === 'true';
			}

			calculatedFlags[flag] = isActive;
		});

		return { ...calculatedFlags, ...overrides };
	}, [overrides, serverFeatureFlags]);

	const isFeatureFlagActive = useCallback(
		(id: FeatureFlagId) => !!flags[id],
		[flags]
	);

	const value = useMemo(
		() => ({ flags, isFeatureFlagActive }),
		[flags, isFeatureFlagActive]
	);

	return (
		<FeatureFlagsContext.Provider value={value}>
			{children}
		</FeatureFlagsContext.Provider>
	);
};

const useFeatureFlagsContext = () => {
	const context = useContext(FeatureFlagsContext);
	/* istanbul ignore if: no need to test this */
	if (context === undefined) {
		throw new Error(
			'useFeatureFlagsContext must be used within a FeatureFlagsProvider'
		);
	}
	return context;
};

export const useFeatureFlags = () => {
	const { isFeatureFlagActive } = useFeatureFlagsContext();
	return { isFeatureFlagActive };
};
